import React, { useState, useEffect } from 'react';
import { Search, Plus, Eye, Edit, Trash2, Filter, Loader2, RefreshCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import AddEmployeeModal from './AddEmployeeModal';
import ViewEmployeeModal from './ViewEmployeeModal';
import api, { getImageUrl } from '../api';

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [editEmployee, setEditEmployee] = useState(null);
  const [totalItems, setTotalItems] = useState(0);

  const itemsPerPage = 15;

  useEffect(() => {
    fetchEmployees();
  }, [searchTerm, currentPage]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.get('/employees', {
        params: {
          search: searchTerm,
          page: currentPage,
          per_page: itemsPerPage
        }
      });
      const data = response.data.data || [];
      setTotalItems(response.data.total || 0);
      
      const formatTime = (time) => {
        if (!time) return 'N/A';
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
      };

      const getInitials = (name) => {
        if (!name || name === 'N/A' || name === 'Unknown') return 'U';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
          return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
      };

      const transformed = data.map((emp, i) => {
        const empName = emp.name || emp.user?.name || 'Unknown Employee';
        return {
          ...emp,
          id: emp.employee_id || emp.id, // Use employee_id column
          name: empName,
          department: emp.department || 'N/A',
          role: emp.designation || emp.role || 'N/A',
          shift: `${formatTime(emp.shift_start_time)} - ${formatTime(emp.shift_end_time)}`,
          joinDate: emp.joining_date || 'N/A',
          avatar: emp.user?.profile_photo_path ? getImageUrl(emp.user.profile_photo_path) : null,
          initials: getInitials(empName)
        };
      });
      
      setEmployees(transformed);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError('Failed to load employees.');
    } finally {
      setLoading(false);
    }
  };

  // Delete handler
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this employee deletion!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/employees/${id}`);
        setEmployees(prev => prev.filter(e => e.id !== id));
        toast.success('Employee deleted successfully');
      } catch (err) {
        toast.error('Failed to delete employee');
      }
    }
  };

  // Edit handler - opens modal with pre-filled data
  const handleEdit = (emp) => {
    setEditEmployee(emp);
    setIsAddModalOpen(true);
  };

  // Filtered - Removed client-side filter as backend handles it now
  const filtered = employees; 

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentData = employees;

  // Removed early return for loading to prevent search bar unmounting and focus loss
  if (error) {
     return <div className="p-20 text-center text-red-500 font-bold">{error}</div>;
  }

  return (
    <div className="space-y-1.5">
      
      {/* Search & Add Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
        <h2 className="text-xl font-black text-slate-800 tracking-tight">Employee Management</h2>
        
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Search employees by name, email, phone, department..." 
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 w-full transition-all"
            />
          </div>
          <button 
            onClick={() => { setEditEmployee(null); setIsAddModalOpen(true); }}
            className="bg-[#10b981] hover:bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-bold flex items-center gap-2 transition-colors flex-shrink-0 shadow-sm"
          >
            <Plus size={18} strokeWidth={3} />
            Add Employee
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center backdrop-blur-[1px]">
             <Loader2 className="animate-spin text-emerald-500" size={32} />
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-100 text-[11px] uppercase text-gray-500 tracking-wide">
                <th className="px-6 py-4 font-black">ID & NAME</th>
                <th className="px-6 py-4 font-black">DEPARTMENT</th>
                <th className="px-6 py-4 font-black">ROLE</th>
                <th className="px-6 py-4 font-black">SHIFT TIMING</th>
                <th className="px-6 py-4 font-black">JOINING DATE</th>
                <th className="px-6 py-4 text-right font-black">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentData.map((emp) => (
                <tr key={emp.id} className="hover:bg-gray-50 transition-colors group">
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      {emp.avatar ? (
                        <img src={emp.avatar} alt={emp.name} className="w-10 h-10 rounded-full object-cover border border-gray-100 shadow-sm" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-slate-100 text-emerald-600 border border-slate-200 flex items-center justify-center text-sm font-black uppercase shadow-sm">
                          {emp.initials}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-bold text-slate-800 leading-tight mb-0.5">{emp.name}</p>
                        <p className="text-[11px] font-bold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded inline-block">{emp.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <span className="text-sm font-bold text-slate-700">{emp.department}</span>
                  </td>
                  <td className="px-6 py-3">
                    <span className="text-sm font-bold text-slate-700">{emp.role}</span>
                  </td>
                  <td className="px-6 py-3">
                    <span className="text-[11px] font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-md tracking-wide">{emp.shift}</span>
                  </td>
                  <td className="px-6 py-3">
                    <span className="text-sm font-bold text-slate-700">{emp.joinDate}</span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex items-center justify-end gap-2 transition-opacity">
                      {/* View */}
                      <button 
                        onClick={() => setViewEmployee(emp)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-100 transition-colors bg-blue-50 p-1.5 rounded-md border border-blue-200 shadow-sm"
                        title="View"
                      >
                        <Eye size={16} strokeWidth={2.5} />
                      </button>
                      {/* Edit */}
                      <button 
                        onClick={() => handleEdit(emp)}
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100 transition-colors bg-emerald-50 p-1.5 rounded-md border border-emerald-200 shadow-sm"
                        title="Edit"
                      >
                        <Edit size={16} strokeWidth={2.5} />
                      </button>
                      {/* Delete */}
                      <button 
                        onClick={() => handleDelete(emp.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-100 transition-colors bg-red-50 p-1.5 rounded-md border border-red-200 shadow-sm"
                        title="Delete"
                      >
                        <Trash2 size={16} strokeWidth={2.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {currentData.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm font-semibold text-slate-400">No employees found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm font-semibold text-slate-500">
          Showing <strong className="text-slate-700">{(currentPage - 1) * itemsPerPage + 1}</strong>–<strong className="text-slate-700">{Math.min(currentPage * itemsPerPage, filtered.length)}</strong> of <strong className="text-slate-700">{filtered.length}</strong> employees
        </p>
        <div className="flex items-center gap-1.5">
          <button 
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-40 transition-colors border border-transparent hover:border-gray-200 shadow-sm"
          >Previous</button>
          
          {totalPages > 1 && Array.from({ length: totalPages }).map((_, idx) => {
            const pageNum = idx + 1;
            if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-bold transition-all shadow-sm ${
                    currentPage === pageNum 
                      ? 'bg-[#10b981] text-white border border-[#10b981]' 
                      : 'text-gray-500 hover:bg-gray-100 border border-transparent hover:border-gray-200 bg-white'
                  }`}
                >{pageNum}</button>
              );
            }
            if ((pageNum === 2 && currentPage > 3) || (pageNum === totalPages - 1 && currentPage < totalPages - 2)) {
              return <span key={pageNum} className="text-gray-400 px-1 font-bold">...</span>;
            }
            return null;
          })}
          
          <button 
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-40 transition-colors border border-transparent hover:border-gray-200 shadow-sm"
          >Next</button>
        </div>
      </div>

      {/* Modals */}
      <AddEmployeeModal 
        isOpen={isAddModalOpen} 
        onClose={() => { setIsAddModalOpen(false); setEditEmployee(null); }}
        editData={editEmployee}
        onSuccess={() => {
          setIsAddModalOpen(false);
          setEditEmployee(null);
          fetchEmployees();
        }}
      />
      <ViewEmployeeModal 
        isOpen={!!viewEmployee} 
        onClose={() => setViewEmployee(null)} 
        employee={viewEmployee}
      />
    </div>
  );
}
